import { useCallback, useMemo } from "react";
import { useSearchParams } from "react-router-dom";

interface IUrlParamsConfig {
    [key: string]: {
        defaultValue: string | number;
        type: "string" | "number";
        validValues?: (string | number)[];
    };
}

interface IUseUrlParamsReturn<T extends Record<string, string | number>> {
    params: T;
    initialParams: T;
    updateParams: (newParams: Partial<T>) => void;
    resetParams: () => void;
}

export function useUrlParams<T extends Record<string, string | number>>(
    config: IUrlParamsConfig,
): IUseUrlParamsReturn<T> {
    const [searchParams, setSearchParams] = useSearchParams();

    // URL에서 현재 파라미터 값들 읽기
    const params = useMemo(() => {
        const result = {} as T;

        Object.entries(config).forEach(([key, { defaultValue, type, validValues }]) => {
            const urlValue = searchParams.get(key);

            if (urlValue !== null) {
                if (type === "number") {
                    const numValue = Number(urlValue);
                    // 유효한 숫자인지 확인 (NaN, Infinity 제외)
                    if (!isNaN(numValue) && isFinite(numValue)) {
                        // validValues가 있으면 허용된 값인지 확인
                        if (validValues && !validValues.includes(numValue)) {
                            console.warn(
                                `Invalid number value for ${key}: "${urlValue}" (not in allowed values) → using default: ${defaultValue}`,
                            );
                            result[key as keyof T] = defaultValue as T[keyof T];
                        } else {
                            result[key as keyof T] = numValue as T[keyof T];
                        }
                    } else {
                        console.warn(`Invalid number value for ${key}: "${urlValue}" → using default: ${defaultValue}`);
                        result[key as keyof T] = defaultValue as T[keyof T];
                    }
                } else if (type === "string") {
                    // validValues가 있으면 허용된 값인지 확인
                    if (validValues && !validValues.includes(urlValue)) {
                        console.warn(
                            `Invalid string value for ${key}: "${urlValue}" (not in allowed values) → using default: ${defaultValue}`,
                        );
                        result[key as keyof T] = defaultValue as T[keyof T];
                    } else {
                        result[key as keyof T] = urlValue as T[keyof T];
                    }
                }
            } else {
                result[key as keyof T] = defaultValue as T[keyof T];
            }
        });

        return result;
    }, [searchParams, config]);

    // 초기값들 (컴포넌트가 처음 마운트될 때의 값들)
    const initialParams = useMemo(() => {
        const result = {} as T;

        Object.entries(config).forEach(([key, { defaultValue }]) => {
            result[key as keyof T] = defaultValue as T[keyof T];
        });

        return result;
    }, [config]);

    // URL 파라미터 업데이트 함수
    const updateParams = useCallback(
        (newParams: Partial<T>) => {
            const newSearchParams = new URLSearchParams(searchParams);

            Object.entries(newParams).forEach(([key, value]) => {
                if (value !== undefined && value !== null && value !== "") {
                    newSearchParams.set(key, String(value));
                } else {
                    newSearchParams.delete(key);
                }
            });

            setSearchParams(newSearchParams, { replace: true });
        },
        [searchParams, setSearchParams],
    );

    // 기본값으로 리셋
    const resetParams = useCallback(() => {
        const defaultParams = {} as Partial<T>;

        Object.entries(config).forEach(([key, { defaultValue }]) => {
            defaultParams[key as keyof T] = defaultValue as T[keyof T];
        });

        updateParams(defaultParams);
    }, [config, updateParams]);

    return {
        params,
        initialParams,
        updateParams,
        resetParams,
    };
}
